type Data = Record<string, any>;

/**
 * Returns result of localeCompare between a.f & b.f
 * @param f field f {string} field to sort in ascending mode
 * @param a object a
 * @param b object b
 *
 */
const localAndLowerCompare = (a, b) => `${a}`.toLocaleLowerCase().localeCompare(`${b}`.toLocaleLowerCase());

/**
 * invokes localAndLowerCompare
 * @param f {string} field to sort in ascending mode
 * @param a object a
 * @param b object b
 */
export const doSort = (f, a, b) => {
  if (a[f] === undefined) {
    return -1;
  }
  return localAndLowerCompare(a[f] || '', b[f] || '');
};

/**
 * invokes localAndLowerCompare in opposite order
 * @param f {string} field to sort in ascending mode
 * @param a object a
 * @param b object b
 */
export const sortDescending = (f, a, b) => doSort(f, b, a);

/**
 * factory function to return a sorting function
 * @param f
 * @param sortFn
 */
const baseSortFactory = (f, sortFn) => sortFn.bind(null, f);

/**
 * Creates a sorting fn in ascending mode binding a field
 * @param f {string} field to sort in ascending mode
 */
export const sortAscFactory = f => baseSortFactory(f, doSort);

/**
 * Creates a sorting fn in descending mode binding a field
 * @param f {string} field to sort in descending mode
 *
 * @example
 * const byMyFieldAsc = sortAscFactory('myFieldToCompare');
 * collection.sort(byMyFieldAsc)
 */
export const sortDesFactory = f => baseSortFactory(f, sortDescending);

/**
 * Returns a number based upon the values. This can be passed to sort function to customize the sort.
 * @param a {any} field to sort in ascending mode
 * @param b {any} field to sort in ascending mode
 */
export const standardComparator = (a, b) => {
  return a - b;
};

/**
 * Returns a number based upon the strings. This can be passed to sort function to customize the sort.
 *  @param a {string} field to sort in ascending mode
 *  @param b {string} field to sort in ascending mode
 */
export const stringComparator = (a, b) => {
  return a.localeCompare(b);
};

/**
 * Returns a number based upon the values of Dates. This can be passed to sort function to customize the sort.
 * @param dateOne {date} field to sort in ascending mode
 * @param dateTwo {date} field to sort in ascending mode
 */
export const dateComparator = (dateOne, dateTwo) => {
  dateOne = dateParse(dateOne);
  dateTwo = dateParse(dateTwo);
  return standardComparator(dateOne, dateTwo);
};

const dateParse = date => {
  const _date = Date.parse(date);
  return isNaN(_date) ? null : _date;
};

export const sortObjectBy = (sortOn, sortType) => {
  const sortOrder = sortType === 'desc' ? -1 : 1;
  return (a, b) => {
    if (a[sortOn] < b[sortOn]) {
      return sortOrder * -1;
    }
    return a[sortOn] > b[sortOn] ? sortOrder * 1 : sortOrder * 0;
  };
};

export const numberComparator = (leftValue, rightValue) => {
  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);
  return standardComparator(leftNumber, rightNumber);
};

export const SORT_BY_DATATYPE = {
  text: (a, b) => stringComparator(a, b),
  number: (a, b) => standardComparator(a, b),
  date: (a, b) => dateComparator(a, b),
};
const groupByMulti = (multiSort: any[], records: Data[], path: string, counter: number): Data[] => {
  if (multiSort.length === counter) {
    return records;
  }
  const { dataType, order, field } = multiSort[counter];

  const uniqueKeysData = records.reduce((acc, record) => {
    // for each record get the field value in the record as it becomes key in acc.
    const fieldKey = path ? record[path][0][field] : record[field];
    // get the value if there are any for the key which is just extrcted.
    const currentKeyRecords = acc[fieldKey];
    // If there are any values apppend the record to currentKeyRecords
    return currentKeyRecords
      ? { ...acc, [fieldKey]: [...currentKeyRecords, record] }
      : { ...acc, [fieldKey]: [record] };
  }, {});

  // Sort the keys based upon the dataType. We are using the comparators which are used for ag-grid.
  const sortedKeys = Object.keys(uniqueKeysData).sort(SORT_BY_DATATYPE[dataType]);
  if (order === 'desc') {
    sortedKeys.reverse();
  }

  const accData = [];
  // Now that keys are sorted, pass the grouped data to the function again with comparator incremented,
  // the grouped data now gets sub-groped and sorted based on next obj in multiSort.
  sortedKeys.forEach(key => {
    accData.push(groupByMulti(multiSort, uniqueKeysData[key], path, counter + 1));
  });
  return accData;
};

function flatDeep(arr: Data[], counter = 1) {
  return counter > 0
    ? arr.reduce((acc, val) => acc.concat(Array.isArray(val) ? flatDeep(val, counter - 1) : val), [])
    : arr.slice();
}

export function compareAndSetNullsLast(aValue, bValue): number {
  let order;
  if (aValue == null && bValue != null) {
    order = -1;
  } else if (aValue != null && bValue == null) {
    order = 1;
  } else if (aValue == null && bValue == null) {
    order = 0;
  } else {
    order = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
  }

  return order;
}

export const groupAndSortByMulti = (multiSort: any[], records: Data[], path: string) => {
  return flatDeep(groupByMulti(multiSort, records, path, 0), multiSort.length);
};

/**
 *  Compare two values and returns:
 *  a Negative number if the first value is Before the second
 *  a Positive number if the first value is After the second
 * @param leftSingleValue
 * @param rightSingleValue
 * @returns number
 */
export const comparedTwoValuesByValueType = (leftSingleValue: any, rightSingleValue: any): number => {
  // The possible values for a number field could be: *****, numbers, null
  let leftValue = (leftSingleValue ?? '') + '';
  let rightValue = (rightSingleValue ?? '') + '';
  if (!isNaN(Number(leftValue.replace(/,/g, ''))) && !isNaN(Number(rightValue.replace(/,/g, '')))) {
    // Handling numbers with comma separator
    return numberComparator((leftValue || '-1').replace(/,/g, ''), (rightValue || '-1').replace(/,/g, ''));
  } else if (isAValidDate(leftValue)) {
    return dateComparator(leftValue, rightValue);
  } else {
    return stringComparator(leftValue, rightValue);
  }
};

/**
 * Validate if a value is a valid date
 * @param value string with specific format YYYY-MM-DDTHH:mm:ssZ | YYYY/MM/DD | YYYY-MM-DD
 * @returns boolean
 */
export const isAValidDate = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  // Weird values are evaluated as date like: Radke-123
  const possibleDate = value.split('T')[0];
  return (
    !!possibleDate.match(/^[0-9]{4}(-|\/){1}(0[1-9]|1[0-2]|[1-9])(-|\/){1}(0[1-9]|3[01]|[12][0-9]|[1-9])$/) &&
    !isNaN(Date.parse(possibleDate))
  );
};
